import React, { useState, useEffect } from 'react';
import { Button } from '@/shared/shadcn/ui/Button';
import { Avatar, AvatarImage, AvatarFallback } from '@/shared/shadcn/ui/avatar';
import { getInitials } from '@/shared/utils/stringUtils';
import { FacilityOption } from '@/features/Auth/types';

interface FacilitySelectionProps {
  facilities: FacilityOption[];
  onSelect: (index: number) => void;
  currentPracticeId?: number;
  description?: string;
}

const FacilitySelection: React.FC<FacilitySelectionProps> = ({ facilities, onSelect, currentPracticeId, description = 'Please choose a facility to proceed' }) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // Set current facility as default selected when currentPracticeId is provided
  useEffect(() => {
    if (currentPracticeId) {
      const currentIndex = facilities.findIndex(
        f => f.practiceInfo.id === currentPracticeId
      );
      if (currentIndex >= 0) {
        setSelectedIndex(currentIndex);
      }
    }
  }, [currentPracticeId, facilities]);

  const handleProceed = () => {
    if (selectedIndex !== null) {
      onSelect(selectedIndex);
    }
  };

  return (
    <div 
      className="w-full max-w-lg p-6 bg-white rounded-2xl shadow-[0px_3px_8px_#0000003d] border border-gray-100"
      data-testid="facility-selection-container"
    >
      <h2 className="text-2xl font-bold mb-1.5 text-center text-hh-primary">
        Choose Facility
      </h2>
      <p id="facility-description" className="text-gray-600 text-sm text-center mb-6">
        {description}
      </p>

      <div 
        className="space-y-3 mb-6 max-h-[400px] overflow-y-auto"
        role="radiogroup"
        aria-labelledby="facility-selection-heading"
        aria-describedby="facility-description"
        data-testid="facility-list"
      >
        {facilities.map((facility, index) => {
          const isCurrentFacility = currentPracticeId && facility.practiceInfo.id === currentPracticeId;
          
          return (
            <div
              key={facility.practiceInfo.id}
              onClick={() => setSelectedIndex(index)}
              role="radio"
              aria-checked={selectedIndex === index}
              aria-label={`${facility.practiceInfo.practiceName}${isCurrentFacility ? ' (Current)' : ''}`}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedIndex(index);
                }
              }}
              data-testid={`facility-option-${index}`}
              className={`p-4 border rounded-lg cursor-pointer transition-all ${
                selectedIndex === index
                  ? 'border-hh-primary bg-hh-primary/5'
                  : 'border-gray-200 hover:border-hh-primary/50'
              }`}
            >
              <div className="flex items-start gap-3">
                {/* Facility Icon/Avatar */}
                <Avatar className="w-11 h-11">
                  <AvatarImage 
                    src={facility.practiceInfo.logoUrl} 
                    alt={facility.practiceInfo.practiceName}
                    className="object-contain"
                  />
                  <AvatarFallback 
                    id={facility.practiceInfo.id.toString()}
                    className="font-medium text-md"
                  >
                    {getInitials(facility.practiceInfo.practiceName)}
                  </AvatarFallback>
                </Avatar>

                {/* Facility Details */}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900">
                      {facility.practiceInfo.practiceName}
                    </h3>
                    {isCurrentFacility && (
                      <span 
                        className="text-xs bg-hh-primary text-white px-2 py-0.5 rounded"
                        data-testid="current-facility-badge"
                      >
                        Current
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    {facility.practiceInfo.address}, {facility.practiceInfo.city}, {facility.practiceInfo.state} {facility.practiceInfo.zip}
                  </p>
                </div>

                {/* Radio indicator */}
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  selectedIndex === index
                    ? 'border-hh-primary'
                    : 'border-gray-300'
                }`}>
                  {selectedIndex === index && (
                    <div className="w-3 h-3 rounded-full bg-hh-primary" />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Button
        onClick={handleProceed}
        disabled={selectedIndex === null}
        className="w-full"
        data-testid="proceed-button"
        aria-disabled={selectedIndex === null}
      >
        Proceed
      </Button>
    </div>
  );
};

export default FacilitySelection;

